import { useState, useEffect } from "react";
import { Mail, Share2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useSetAtom } from 'jotai';
import { campaignWizardAtom } from '@/lib/atoms';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { captureError } from '@/lib/sentry';

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface ViewListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
  listName: string;
}

export function ViewListDialog({
  open,
  onOpenChange,
  listId,
  listName,
}: ViewListDialogProps) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const setCampaignWizard = useSetAtom(campaignWizardAtom);
  const { toast } = useToast();

  const fetchContacts = async () => {
    try {
      const { data: contactsData, error: contactsError } = await supabase
        .from('list_members')
        .select(`
          contact_id,
          contacts:contacts_with_primary_details (
            id,
            first_name,
            last_name,
            primary_email
          )
        `)
        .eq('list_id', listId);

      if (contactsError) throw contactsError;

          //.eq('email_address.is_primary', true);
      const formattedContacts = contactsData
        .map(row => Array.isArray(row.contacts) ? row.contacts[0] : row.contacts)
        .filter(contact => contact?.primary_email) // Only include contacts with email addresses
        .map(contact => ({
          id: contact?.id || '',
          first_name: contact?.first_name || '',
          last_name: contact?.last_name || '',
          email: contact?.primary_email || ''
        }));

      setContacts(formattedContacts);
    } catch (error) {
      console.error('Error fetching list contacts:', error);
      captureError(error as Error, { feature: 'lists-view-list-dialog-fetch-contacts' });
      toast({
        title: "Error",
        description: "Failed to load contacts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchContacts();
    }
  }, [open, listId]);

  const removeContact = async (contactId: string) => {
    try {
      const { error } = await supabase
        .from('list_members')
        .delete()
        .eq('list_id', listId)
        .eq('contact_id', contactId);

      if (error) throw error;

      toast({
        description: "Contact removed from list",
      });

      fetchContacts();
    } catch (error) {
      captureError(error as Error, { feature: 'lists-view-list-dialog-remove-contact' });
      toast({
        title: "Error",
        description: "Failed to remove contact",
        variant: "destructive",
      });
    }
  };

  // const emailAll = () => {
  //   const validEmails = contacts
  //     .map(contact => contact.email)
  //     .filter(Boolean);

  //   if (validEmails.length === 0) {
  //     toast({
  //       title: "No email addresses",
  //       description: "None of the contacts have email addresses.",
  //       variant: "destructive",
  //     });
  //     return;
  //   }

  //   window.location.href = `mailto:${validEmails.join(',')}`;
  // };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{listName}</DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const validContacts = contacts.filter(c => c.email);
                  setCampaignWizard({
                    open: true,
                    selectedContacts: validContacts.map(c => ({
                      id: c.id,
                      first_name: c.first_name,
                      last_name: c.last_name,
                      primary_email: c.email,
                    })) as any,
                  });
                }}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email List
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                Share List
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="mt-4">
          {loading ? (
            <div className="text-center py-4">Loading contacts...</div>
          ) : contacts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      {contact.first_name} {contact.last_name}
                    </TableCell>
                    <TableCell>{contact.email}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (contact.email) {
                              window.location.href = `mailto:${contact.email}`;
                            } else {
                              toast({
                                title: "No email address",
                                description: "This contact doesn't have an email address.",
                                variant: "destructive",
                              });
                            }
                          }}
                          disabled={!contact.email}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeContact(contact.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              No contacts in this list
            </div>
          )}
        </div>
      </DialogContent>
      
    </Dialog>
  );
}